import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { AlertCircle, ClipboardCheck, PackageOpen, ShieldCheck, Search } from "lucide-react";

const TABS = [
  { id: "pending_approvals", label: "My Pending Approvals", icon: ClipboardCheck },
  { id: "grn", label: "GRN Alerts", icon: PackageOpen },
  { id: "low_stock", label: "Low / Over Stock", icon: AlertCircle },
  { id: "qa", label: "QA Alerts", icon: ShieldCheck },
];

export default function Alerts() {
  const [data, setData] = useState({ grn: [], low_stock: [], qa: [], pending_approvals: [] });
  const [active, setActive] = useState("pending_approvals");
  const [q, setQ] = useState("");

  useEffect(() => {
    const load = () => api.get("/alerts/categorized").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const items = useMemo(() => {
    const list = data[active] || [];
    if (!q) return list;
    const k = q.toLowerCase();
    return list.filter((a) => (a.message || "").toLowerCase().includes(k) || (a.ref || "").toLowerCase().includes(k));
  }, [data, active, q]);

  const counts = {
    grn: data.grn?.length || 0,
    low_stock: data.low_stock?.length || 0,
    qa: data.qa?.length || 0,
    pending_approvals: data.pending_approvals?.length || 0,
  };

  return (
    <div data-testid="alerts-page">
      <PageHeader title="Alerts" subtitle="Categorized · auto-refresh every 30s"
        helpText="Pending Approvals are filtered to your role. GRN tracks Gate Entry → GRN overdue and GRNs awaiting Quality. Low / Over Stock checks every material against min/max. QA shows rejected and NG inspections."/>
      <div className="px-8 pt-6">
        <div className="flex flex-wrap gap-2 mb-4 border-b border-[#EEEEEE]">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setActive(t.id)} data-testid={`alerts-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold border-b-2 transition ${active === t.id ? "border-[#1565C0] text-[#1565C0]" : "border-transparent text-[#0D2B5E]/60 hover:text-[#0D2B5E]"}`}>
              <t.icon size={14}/> {t.label}
              <span className={`ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${active === t.id ? "bg-[#1565C0] text-white" : "bg-[#F5F5F5]"}`}>{counts[t.id]}</span>
            </button>
          ))}
        </div>
        <div className="mb-3 relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D2B5E]/40"/>
          <input data-testid="alerts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search alerts…" className="w-full pl-9 pr-3 py-2 border border-[#EEEEEE] rounded-sm text-sm bg-white"/>
        </div>
      </div>
      <div className="px-8 pb-8">
        <div className="bg-white border border-[#EEEEEE] rounded-sm divide-y divide-[#EEEEEE]">
          {items.map((a, i) => (
            <div key={i} data-testid={`alert-${active}-${i}`} className="px-5 py-3 flex items-start gap-3 hover:bg-[#F5F5F5]/50">
              <div className={`mt-1 w-2 h-2 rounded-full ${a.level === "red" ? "bg-[#C62828]" : "bg-[#E65100]"}`}/>
              <div className="flex-1">
                <div className="text-sm text-[#0D2B5E]">{a.message}</div>
                {a.ref && <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-mono mt-0.5">{a.ref}</div>}
              </div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: a.level === "red" ? "#C62828" : "#E65100" }}>{a.level}</div>
            </div>
          ))}
          {items.length === 0 && <div className="p-12 text-center text-sm text-[#0D2B5E]/50">No alerts in this category.</div>}
        </div>
      </div>
    </div>
  );
}
